import type { RankingConfig } from './rankingCompetition';
import type { RankingReport } from '@/queries/cpmRanking';

/**
 * Canonical clip admission and legacy period reports have independent states.
 * A disqualified clip (its account or its owner was disqualified) is out of
 * review whatever its approval, so it counts as rejected everywhere.
 */
export function rankingReportStatus(report: RankingReport): string {
  if (report.disqualified) return 'rejected';
  if (!report.submission_id) return report.status;
  return report.clip_approval === 'approved' ? 'approved' : report.clip_approval === 'disapproved' ? 'rejected' : 'pending';
}

/** Badge key: like the status, but names a disqualification instead of a rejection. */
export function rankingReportBadge(report: RankingReport): string {
  return report.disqualified ? 'disqualified' : rankingReportStatus(report);
}

/** Unknown provider data must never become a verified or fabricated zero. */
export function rankingCountedViews(report: RankingReport): string | null {
  if (rankingReportStatus(report) !== 'approved') return null;
  const value = report.submission_id ? report.ranked_views : report.profile_verified && report.ownership_verified ? report.verified_views : null;
  return value != null && /^\d+$/.test(value) ? value : null;
}

/** Do all count arithmetic as integers; rounding is only for the progress bar. */
export function rankingQualification(views: string, minimum: string): { remaining: string; percent: number } {
  const total = BigInt(views);
  const target = BigInt(minimum);
  if (target <= 0n || total >= target) return { remaining: '0', percent: 100 };
  return { remaining: (target - total).toString(), percent: Number(total * 100n / target) };
}

/** The backend authorizes the immutable own award; config must never create a CTA. */
export function rankingWinnerContact(ranking: {
  phase: string;
  config: Pick<RankingConfig, 'payout_destination'>;
  winner_contact_url?: string | null;
  my_row?: { is_self: boolean; eligible: boolean; tie_pending?: boolean | null; prize_cents: number } | null;
}): string | null {
  const row = ranking.my_row;
  if (ranking.phase !== 'finalized' || ranking.config.payout_destination !== 'external'
    || !row?.is_self || !row.eligible || row.tie_pending || row.prize_cents <= 0) return null;
  const url = ranking.winner_contact_url;
  return typeof url === 'string' && /^https:\/\/wa\.me\/[1-9]\d{6,14}$/.test(url) ? url : null;
}

/** Translate the one system reason while retaining reviewers' text verbatim. */
export function rankingReviewReason(reason: string, t: (key: string) => string): string {
  return reason === 'ranking_automatic_clip_admission' ? t('ranking.diagnostics.automaticAdmission') : reason;
}
